import { useMemo, useState } from 'react';
import Link from 'next/link';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import BaseCheckbox from '@r1-frontend/ui-react/components/controls/baseCheckbox';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { CallCalling } from '@r1-frontend/ui-react/components/svg/call';
import {
    IndentContainer,
    ListContainer,
} from '@r1-frontend/ui-react/experimental/containers';
import SpeedLineWithDescription, {
    TSpeedLineItemWithDescription,
} from '@r1-frontend/ui-react/organisms/SpeedLine/SpeedLineWithDescription';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';
import { TArrayTwoOrMore } from '@r1-frontend/shared/types/Array';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import LazyCallBack from '~/src/components/LazyCallBack';
import getUriToFullBuy from '~/src/features/request/helpers/getUriToFullBuy';
import { TTariffsGroup } from '~/src/features/request/types/TTariffsGroup';
import { getAnalyticsSender } from '~/src/helpers/hooks/useAnaliticsSender';

import * as ST from './styled';

type TSpeedLineSelectorProps = {
    items: {
        bundles: TArrayTwoOrMore<TSpeedLineItemWithDescription>,
        internet: TArrayTwoOrMore<TSpeedLineItemWithDescription>,
    },
    isAuth: boolean,
};

const sendAnalytics = getAnalyticsSender('internet');

const SpeedLineSelector = ({
    items,
    isAuth,
}: TSpeedLineSelectorProps): JSX.Element => {
    const isTablet = useMatchMediaByWidth(wideBreakpoints.tablet);

    const [selectedSpeedItemId, setSelectedSpeedItemId] = useState(0);
    const [isLazyCallbackOpen, setIsLazyCallbackOpen] = useState(false);
    const [tariffsGroup, setTariffsGroup] = useState<TTariffsGroup>(
        items.bundles.length ? 'bundles' : 'internet',
    );

    const selectedSpeedItem = useMemo(
        () => items[tariffsGroup][selectedSpeedItemId],
        [items, tariffsGroup, selectedSpeedItemId],
    );

    const connectLink = isAuth
        ? '/lk/change-tariff'
        : getUriToFullBuy(selectedSpeedItem.alias, tariffsGroup);

    const handleSetIsTvChannelsNeed = () => {
        setTariffsGroup((prev) => prev === 'bundles' ? 'internet' : 'bundles');
        setSelectedSpeedItemId(0);
    };

    const onClickHandler = () => {
        sendAnalytics(
            'click_connect_button',
            `speed:${selectedSpeedItem.speed} Мбит/с`,
        );
    };

    const handleSelectSpeedItemId = (id: number) => {
        setSelectedSpeedItemId(id);
        sendAnalytics('move_speed_slider');
    };

    const handleOpenLazyCallback = () => {
        setIsLazyCallbackOpen(true);
        sendAnalytics('open_call_form');
    };

    const handleOnSubmitLazyCallBack = () => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: 'application_form',
            action: 'finished',
            label: 'short_form | Мы свяжемся с вами в ближайшее время.',
        });
    };

    return (
        <IndentContainer $united={[0, 24]}>
            <ListContainer $size={16} $direction={'column'} $wrap={'nowrap'}>
                {
                    /**
                     * В связи с тем, что наборы тарифов с ТВ и без ТВ могут различаться в количестве и конечным
                     * составом, было принято решение вывести два компонента выбора скорости под каждый набор. Таким
                     * образом с компонента снята ответственность перерендера с новым набором и обнуление состояния
                     * выбранного элемента.
                     *
                     * Это необходимо в связи с тем, что есть внешний контрол (с ТВ, без ТВ), который влияет на набор
                     * тарифов. Вывести два компонента гораздо дешевле чем монструозить логику сброса состояния
                     * выбранной скорости в зависимости от нового набора данных.
                     */
                    tariffsGroup === 'bundles'
                        ? (
                            <SpeedLineWithDescription
                                key={'SpeedLineWithDescription_bundles'}
                                items={items.bundles}
                                onSelect={handleSelectSpeedItemId}
                                title={'Какая скорость интернета вам нужна?'}
                                startDot={0}
                            />
                        )
                        : (
                            <SpeedLineWithDescription
                                key={'SpeedLineWithDescription_internet'}
                                items={items.internet}
                                onSelect={handleSelectSpeedItemId}
                                title={'Какая скорость интернета вам нужна?'}
                                startDot={0}
                            />
                        )
                }

                <BaseCheckbox
                    checked={tariffsGroup === 'bundles'}
                    onChange={handleSetIsTvChannelsNeed}
                >
                    А еще мне нужны ТВ-каналы
                </BaseCheckbox>

                <ST.ButtonGroupWithNowrap isStatic>
                    <Link href={connectLink} passHref>
                        <Button onClick={onClickHandler}>Подключить онлайн</Button>
                    </Link>
                    <Button
                        onClick={handleOpenLazyCallback}
                        rounded
                        bType={'white'}
                    >
                        <CallCalling />
                    </Button>
                </ST.ButtonGroupWithNowrap>
            </ListContainer>

            <DefaultPopup
                isOpen={isLazyCallbackOpen}
                onClose={() => setIsLazyCallbackOpen(false)}
                width={isTablet ? '360px' : '600px'}
            >
                <LazyCallBack
                    tariffName={selectedSpeedItem?.title}
                    requestDataParams={{
                        product_id: tariffsGroup === 'bundles'
                            ? [EProductsId.internet, EProductsId.tv]
                            : [EProductsId.internet],
                    }}
                    withPopupResult
                    title={'Если не хотите заполнять заявку сами'}
                    description={
                        <>
                            Можно сделать это по&nbsp;телефону. Заодно поможем
                            выбрать роутер.
                        </>
                    }
                    onSubmit={handleOnSubmitLazyCallBack}
                />
            </DefaultPopup>
        </IndentContainer>
    );
};

export default SpeedLineSelector;
